/**
 * Template Picker Modal
 * 
 * Shown when creating a new funnel.
 * User can choose:
 * - Start Blank Funnel
 * - Choose from Template (System or My Templates)
 * 
 * Features:
 * - Category tabs (System Templates / My Templates)
 * - Template cards with preview
 * - Lightweight node list preview
 */

import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText,
  Plus,
  Loader2,
  HelpCircle,
  TextCursor,
  MessageSquare,
  FileCheck,
  Calendar,
  GitBranch,
  CheckCircle2,
  Building2,
  User,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useFunnelTemplates, useFunnelTemplate } from "@/hooks/useFunnelTemplates";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

// Node type icons
const NODE_ICONS = {
  question: HelpCircle,
  input: TextCursor,
  message: MessageSquare,
  consent: FileCheck,
  booking: Calendar,
};

// Category display config
const CATEGORY_CONFIG = {
  lead_capture: { label: "Lead Capture", color: "bg-blue-500/10 text-blue-400" },
  qualification: { label: "Qualification", color: "bg-purple-500/10 text-purple-400" },
  appointment: { label: "Appointment", color: "bg-pink-500/10 text-pink-400" },
  insurance: { label: "Insurance", color: "bg-green-500/10 text-green-400" },
  financial: { label: "Financial", color: "bg-amber-500/10 text-amber-400" },
  general: { label: "General", color: "bg-slate-500/10 text-slate-400" },
};

// Template Card Component
function TemplateCard({ template, isSelected, onClick }) {
  const categoryConfig = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.general;
  
  return (
    <button
      className={cn(
        "w-full text-left p-4 rounded-lg border transition-all",
        "hover:border-white/30 hover:bg-white/5",
        isSelected
          ? "border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/50"
          : "border-white/10 bg-white/[0.02]"
      )}
      onClick={onClick}
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-medium text-slate-100">{template.name}</h3>
        <Badge variant="outline" className={cn("text-xs", categoryConfig.color)}>
          {categoryConfig.label}
        </Badge>
      </div>
      <p className="text-sm text-slate-200/60 line-clamp-2 mb-3">
        {template.description || "No description"}
      </p>
      <div className="flex items-center gap-3 text-xs text-slate-200/60">
        <span className="flex items-center gap-1">
          <FileText className="h-3 w-3" />
          {template.node_count} steps
        </span>
        <span>{template.funnel_type}</span>
      </div>
    </button>
  );
}

// Template Preview Component
function TemplatePreview({ templateId }) {
  const { data: template, isLoading } = useFunnelTemplate(templateId);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-slate-200/60" />
      </div>
    );
  }
  
  if (!template) {
    return (
      <div className="flex items-center justify-center h-full text-slate-200/60">
        Select a template to preview
      </div>
    );
  }
  
  const nodes = template.graph?.nodes || [];
  const entryNodeId = template.graph?.entry_node_id;
  
  // Sort nodes: entry first, then by position
  const sortedNodes = [...nodes].sort((a, b) => {
    if (a.id === entryNodeId) return -1;
    if (b.id === entryNodeId) return 1;
    return (a.position?.y || 0) - (b.position?.y || 0);
  });
  
  // Check for branching
  const hasBranching = nodes.some(n => 
    n.node_type === "question" && 
    (n.edges || []).some(e => e.target_node_id)
  );
  
  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-white/10">
        <h3 className="font-semibold text-slate-100 mb-1">{template.name}</h3>
        <p className="text-sm text-slate-200/60">{template.description}</p>
        <div className="flex items-center gap-3 mt-3 text-xs">
          <Badge variant="outline" className="text-slate-200/60">
            {template.node_count} steps
          </Badge>
          {hasBranching && (
            <Badge variant="outline" className="text-blue-400 border-blue-500/30 gap-1">
              <GitBranch className="h-3 w-3" />
              Branching
            </Badge>
          )}
        </div>
      </div>
      
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-2">
          {sortedNodes.map((node, index) => {
            const Icon = NODE_ICONS[node.node_type] || MessageSquare;
            const isEntry = node.id === entryNodeId;
            const isTerminal = node.content?.is_terminal;
            const label = node.metadata?.label || node.node_type;
            
            // Check if has custom routing
            const hasCustomRouting = node.node_type === "question" && 
              (node.edges || []).some(e => e.target_node_id);
            
            return (
              <div
                key={node.id}
                className={cn(
                  "flex items-center gap-3 p-2.5 rounded-md",
                  "bg-white/5 border border-white/10"
                )}
              >
                <div className={cn(
                  "p-1.5 rounded",
                  isTerminal ? "bg-green-500/10" : "bg-white/5"
                )}>
                  <Icon className={cn(
                    "h-4 w-4",
                    isTerminal ? "text-green-400" : "text-slate-200/60"
                  )} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-100 truncate">{label}</span>
                    {isEntry && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 text-slate-200/60">
                        Entry
                      </Badge>
                    )}
                    {isTerminal && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 text-green-400 border-green-500/30">
                        Exit
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-slate-200/60 capitalize">{node.node_type}</span>
                </div>
                {hasCustomRouting && (
                  <GitBranch className="h-4 w-4 text-blue-400" />
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

// Main Modal Component
export default function TemplatePickerModal({
  open,
  onClose,
  onSelectBlank,
  onSelectTemplate,
  onCreated, // Legacy prop - used when onSelectBlank/onSelectTemplate not provided
}) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [funnelName, setFunnelName] = useState("");
  const [funnelSlug, setFunnelSlug] = useState("");
  const [step, setStep] = useState("choose"); // "choose" | "configure"
  const [creating, setCreating] = useState(false);
  
  const { data: templates = [], isLoading } = useFunnelTemplates();
  
  // Separate system and tenant templates
  const systemTemplates = useMemo(() => 
    templates.filter(t => t.is_system_template), [templates]);
  const myTemplates = useMemo(() => 
    templates.filter(t => !t.is_system_template), [templates]);
  
  // Auto-generate slug from name
  const handleNameChange = (e) => {
    const name = e.target.value;
    setFunnelName(name);
    setFunnelSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };
  
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };
  
  const handleContinueWithTemplate = () => {
    if (selectedTemplate) {
      setStep("configure");
    }
  };
  
  const handleCreateFunnel = async () => {
    if (!funnelName || !funnelSlug) return;
    
    if (selectedTemplate) {
      // If onSelectTemplate provided, use it; otherwise handle internally
      if (onSelectTemplate) {
        onSelectTemplate({
          templateId: selectedTemplate.id,
          name: funnelName,
          slug: funnelSlug,
        });
      } else if (onCreated) {
        // Handle template creation internally via API
        setCreating(true);
        try {
          const res = await api.post(`/funnels/from-template/${selectedTemplate.id}`, null, {
            params: { name: funnelName, slug: funnelSlug }
          });
          toast({ title: "Funnel created from template" });
          onCreated(res.data.id);
        } catch (err) {
          const message = err.response?.data?.detail || "Failed to create funnel";
          toast({ title: "Error", description: message, variant: "destructive" });
        } finally {
          setCreating(false);
        }
      }
    }
  };
  
  const handleStartBlank = () => {
    setSelectedTemplate(null);
    setStep("configure");
  };
  
  const handleCreateBlank = async () => {
    if (!funnelName || !funnelSlug) return;
    
    // If onSelectBlank provided, use it; otherwise handle internally
    if (onSelectBlank) {
      onSelectBlank({ name: funnelName, slug: funnelSlug });
    } else if (onCreated) {
      // Handle blank funnel creation internally via API
      setCreating(true);
      try {
        const res = await api.post("/funnels", {
          name: funnelName,
          slug: funnelSlug,
          funnel_type: "conversational"
        });
        toast({ title: "Blank funnel created" });
        onCreated(res.data.id);
      } catch (err) {
        const message = err.response?.data?.detail || "Failed to create funnel";
        toast({ title: "Error", description: message, variant: "destructive" });
      } finally {
        setCreating(false);
      }
    }
  };
  
  const handleBack = () => {
    setStep("choose");
    setFunnelName("");
    setFunnelSlug("");
  };
  
  const handleClose = () => {
    if (creating) return; // Don't close while creating
    setStep("choose");
    setSelectedTemplate(null);
    setFunnelName("");
    setFunnelSlug("");
    setCreating(false);
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn(
        "glass border-white/10 text-slate-100 max-w-4xl p-0 gap-0",
        step === "choose" ? "h-[600px]" : "max-w-md"
      )}>
        {step === "choose" ? (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle className="text-slate-100">Create New Funnel</DialogTitle>
              <DialogDescription className="text-slate-200/60">
                Start from scratch or choose a template to get started quickly
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-1 overflow-hidden">
              {/* Left: Template List */}
              <div className="w-1/2 border-r border-white/10 flex flex-col">
                {/* Blank Option */}
                <div className="p-4 border-b border-white/10">
                  <button
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-lg border border-dashed transition-all",
                      "border-white/20 hover:border-white/40 hover:bg-white/5"
                    )}
                    onClick={handleStartBlank}
                    data-testid="start-blank-btn"
                  >
                    <div className="p-2 rounded-md bg-white/5">
                      <Plus className="h-5 w-5 text-slate-200/70" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-slate-100">Start Blank</div>
                      <div className="text-xs text-slate-200/60">Create a funnel from scratch</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-200/40 ml-auto" />
                  </button>
                </div>
                
                {/* Template Tabs */}
                <Tabs defaultValue="system" className="flex-1 flex flex-col">
                  <div className="px-4 pt-3">
                    <TabsList className="w-full">
                      <TabsTrigger value="system" className="flex-1 gap-1">
                        <Building2 className="h-3.5 w-3.5" />
                        System Templates
                      </TabsTrigger>
                      <TabsTrigger value="my" className="flex-1 gap-1">
                        <User className="h-3.5 w-3.5" />
                        My Templates
                      </TabsTrigger>
                    </TabsList>
                  </div>
                  
                  <TabsContent value="system" className="flex-1 m-0 mt-0">
                    <ScrollArea className="h-[350px] p-4">
                      {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                          <Loader2 className="h-6 w-6 animate-spin text-slate-200/60" />
                        </div>
                      ) : systemTemplates.length === 0 ? (
                        <div className="text-center text-slate-200/60 py-8">
                          No system templates available
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {systemTemplates.map((template) => (
                            <TemplateCard
                              key={template.id}
                              template={template}
                              isSelected={selectedTemplate?.id === template.id}
                              onClick={() => handleSelectTemplate(template)}
                            />
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="my" className="flex-1 m-0 mt-0">
                    <ScrollArea className="h-[350px] p-4">
                      {isLoading ? (
                        <div className="flex items-center justify-center h-32">
                          <Loader2 className="h-6 w-6 animate-spin text-slate-200/60" />
                        </div>
                      ) : myTemplates.length === 0 ? (
                        <div className="text-center text-slate-200/60 py-8">
                          <p>No templates yet</p>
                          <p className="text-sm mt-1">Save a funnel as template to see it here</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {myTemplates.map((template) => (
                            <TemplateCard
                              key={template.id}
                              template={template}
                              isSelected={selectedTemplate?.id === template.id}
                              onClick={() => handleSelectTemplate(template)}
                            />
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </div>
              
              {/* Right: Preview */}
              <div className="w-1/2 flex flex-col bg-white/[0.02]">
                {selectedTemplate ? (
                  <TemplatePreview templateId={selectedTemplate.id} />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-slate-200/60">
                    <div className="text-center">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>Select a template to preview</p>
                    </div>
                  </div>
                )}
                
                {selectedTemplate && (
                  <div className="p-4 border-t border-white/10">
                    <Button
                      className="w-full"
                      onClick={handleContinueWithTemplate}
                      data-testid="use-template-btn"
                    >
                      Use This Template
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Configure Step */
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-slate-100">
                {selectedTemplate ? `Create from "${selectedTemplate.name}"` : "Create Blank Funnel"}
              </DialogTitle>
              <DialogDescription className="text-slate-200/60">
                Enter a name and URL slug for your new funnel
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="funnel-name" className="text-sm font-semibold">Funnel Name</Label>
                <Input
                  id="funnel-name"
                  value={funnelName}
                  onChange={handleNameChange}
                  placeholder="e.g., Mortgage Protection Q4"
                  autoFocus
                  className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid="funnel-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="funnel-slug" className="text-sm font-semibold">URL Slug</Label>
                <Input
                  id="funnel-slug"
                  value={funnelSlug}
                  onChange={(e) => setFunnelSlug(e.target.value)}
                  placeholder="mortgage-protection-q4"
                  className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid="funnel-slug-input"
                />
                <p className="text-xs text-slate-200/60">
                  Used in the funnel URL: /funnel?funnel={funnelSlug || "your-slug"}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={handleBack} className="border-white/10" disabled={creating}>
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={selectedTemplate ? handleCreateFunnel : handleCreateBlank}
                disabled={!funnelName || !funnelSlug || creating}
                data-testid="create-funnel-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Funnel"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
